<app-layout>
  <div class="main-container">
    <mat-card class="chart-card">
      <mat-card-title class="header">
        <h5>{{ title }}</h5>
      </mat-card-title>

      <mat-card-content class="chart-content">
        <div class="chart-toolbar">
          <mat-form-field appearance="fill" class="search-field" subscriptSizing="dynamic">
            <mat-label>Search</mat-label>
            <mat-icon matPrefix class="material-icons-outlined" aria-hidden="true">search</mat-icon>
            <input matInput [formControl]="searchControl" [matAutocomplete]="searchAutocomplete"
                   [placeholder]="chartType === 'organizations' ? 'Organization or employee' : 'Position or employee'">
            <mat-autocomplete #searchAutocomplete="matAutocomplete" [displayWith]="displaySearchItem"
                              (optionSelected)="onSearchSelected($event)" class="org-chart-search-panel">
              <mat-option *ngFor="let item of searchResults" [value]="item">
                <mat-icon class="material-icons-outlined" aria-hidden="true">
                  {{ item.node.type === 'employee' ? 'person' : item.node.type === 'organization' ? 'corporate_fare' : 'work' }}
                </mat-icon>
                <span class="search-label">{{ item.label }}</span>
                <span class="search-hint" *ngIf="item.hint">{{ item.hint }}</span>
              </mat-option>
            </mat-autocomplete>
          </mat-form-field>

          <div class="toolbar-buttons">
            <button mat-stroked-button setColor="primaryColour" color="primary" (click)="expandAll()" [disabled]="isLoading">
              <mat-icon class="material-icons-outlined" aria-hidden="true">unfold_more</mat-icon>Expand All
            </button>
            <button mat-stroked-button setColor="primaryColour" color="primary" (click)="collapseAll()" [disabled]="isLoading">
              <mat-icon class="material-icons-outlined" aria-hidden="true">unfold_less</mat-icon>Collapse All
            </button>
            <button mat-stroked-button setColor="primaryColour" color="primary" (click)="toggleAllEmployees()" [disabled]="isLoading">
              <mat-icon class="material-icons-outlined" aria-hidden="true">groups</mat-icon>{{ showAllEmployees ? 'Hide Employees' : 'Show Employees' }}
            </button>
            <button mat-stroked-button setColor="primaryColour" color="primary" [matMenuTriggerFor]="layoutMenu">
              <mat-icon class="material-icons-outlined" aria-hidden="true">account_tree</mat-icon>Layout
            </button>
            <button *ngIf="chartType === 'positions'" mat-stroked-button setColor="primaryColour" color="primary"
                    (click)="showFilters = !showFilters">
              <mat-icon class="material-icons-outlined" aria-hidden="true">filter_list</mat-icon>
              {{ showFilters ? 'Hide Filters' : 'Show Filters' }}<ng-container *ngIf="activeFilterCount"> ({{ activeFilterCount }})</ng-container>
            </button>
            <button *ngIf="focusRoot" mat-stroked-button setColor="primaryColour" color="primary" (click)="showAll()">
              <mat-icon class="material-icons-outlined" aria-hidden="true">visibility</mat-icon>Show All
            </button>
            <button mat-flat-button setColor="primaryColour" color="primary" [matMenuTriggerFor]="exportMenu"
                    [disabled]="isLoading || isExporting || !roots.length">
              <mat-icon class="material-icons-outlined" aria-hidden="true">download</mat-icon>Export
            </button>
          </div>
        </div>

        <div class="filters" *ngIf="showFilters && chartType === 'positions'">
          <mat-form-field *ngFor="let definition of filterDefinitions" appearance="fill" subscriptSizing="dynamic">
            <mat-label>{{ definition.label }}</mat-label>
            <mat-select multiple [(ngModel)]="filterValues[definition.key]" (selectionChange)="applyFilters()"
                        [disabled]="!definition.options.length">
              <mat-option *ngFor="let option of definition.options" [value]="option">{{ option }}</mat-option>
            </mat-select>
          </mat-form-field>
          <button mat-button setColor="primaryColour" color="primary" (click)="clearFilters()" [disabled]="!activeFilterCount">
            Clear Filters
          </button>
        </div>

        <mat-progress-bar *ngIf="isLoading || isExporting" mode="indeterminate"></mat-progress-bar>
        <div class="export-progress" *ngIf="exportProgress">{{ exportProgress }}</div>

        <div class="chart-area">
          <app-hierarchy-chart [roots]="displayRoots" [orientation]="orientation" [compact]="layout === 'compact'" [exportMode]="isExporting"
                               [nodeTemplate]="nodeTemplate" [visibleUids]="visibleUids"></app-hierarchy-chart>

          <div class="zoom-controls" *ngIf="chart">
            <button mat-icon-button (click)="chart.zoomIn()" matTooltip="Zoom in" matTooltipPosition="left" aria-label="Zoom in">
              <mat-icon class="material-icons-outlined" aria-hidden="true">zoom_in</mat-icon>
            </button>
            <button mat-icon-button (click)="chart.zoomOut()" matTooltip="Zoom out" matTooltipPosition="left" aria-label="Zoom out">
              <mat-icon class="material-icons-outlined" aria-hidden="true">zoom_out</mat-icon>
            </button>
            <button mat-icon-button (click)="chart.fit()" matTooltip="Fit to screen" matTooltipPosition="left" aria-label="Fit to screen">
              <mat-icon class="material-icons-outlined" aria-hidden="true">fit_screen</mat-icon>
            </button>
            <button mat-icon-button (click)="chart.resetZoom()" matTooltip="Actual size" matTooltipPosition="left" aria-label="Actual size">
              <mat-icon class="material-icons-outlined" aria-hidden="true">crop_free</mat-icon>
            </button>
            <span class="zoom-level">{{ chart.zoom * 100 | number: '1.0-0' }}%</span>
          </div>

          <div class="empty-state" *ngIf="!isLoading && !roots.length">
            No {{ chartType === 'organizations' ? 'organizations' : 'positions' }} to display.
          </div>
          <div class="empty-state" *ngIf="!isLoading && roots.length && visibleUids && !visibleUids.size">
            No positions match the selected filters.
          </div>
        </div>
      </mat-card-content>
    </mat-card>
  </div>
</app-layout>

<ng-template #nodeTemplate let-node>
  <app-org-chart-node-card [node]="node" [editable]="editable" [highlightedUid]="highlightedUid" [continuedOn]="continuedOn(node)"
                           (toggle)="toggleNode($event)" (action)="onNodeAction($event)"></app-org-chart-node-card>
</ng-template>

<mat-menu #exportMenu="matMenu">
  <!-- Stays open when clicked so it can be set before choosing a format -->
  <button mat-menu-item class="export-option" (click)="$event.stopPropagation(); exportEmployees = !exportEmployees">
    <mat-icon class="material-icons-outlined" aria-hidden="true">{{ exportEmployees ? 'check_box' : 'check_box_outline_blank' }}</mat-icon>Include employees
  </button>
  <button mat-menu-item [matMenuTriggerFor]="pdfMenu">
    <mat-icon class="material-icons-outlined" aria-hidden="true">picture_as_pdf</mat-icon>PDF
  </button>
  <button mat-menu-item [matMenuTriggerFor]="pngMenu">
    <mat-icon class="material-icons-outlined" aria-hidden="true">image</mat-icon>Image (PNG)
  </button>
  <button mat-menu-item (click)="exportPaged('pptx')">
    <mat-icon class="material-icons-outlined" aria-hidden="true">slideshow</mat-icon>PowerPoint
  </button>
  <button mat-menu-item (click)="export('excel')">
    <mat-icon class="material-icons-outlined" aria-hidden="true">table_view</mat-icon>Excel
  </button>
</mat-menu>

<mat-menu #pdfMenu="matMenu">
  <button mat-menu-item (click)="exportPaged('pdf', 'A4')">Overview + sections (A4)</button>
  <button mat-menu-item (click)="exportPaged('pdf', 'A3')">Overview + sections (A3)</button>
  <button mat-menu-item (click)="export('pdf')">Single page, current view</button>
  <button mat-menu-item (click)="export('pdf', true)">Single page, fully expanded</button>
</mat-menu>

<mat-menu #layoutMenu="matMenu">
  <button mat-menu-item (click)="setLayout('compact')" [class.selected-layout]="layout === 'compact'">
    <mat-icon class="material-icons-outlined" aria-hidden="true">grid_on</mat-icon>Top-down, compact
  </button>
  <button mat-menu-item (click)="setLayout('top')" [class.selected-layout]="layout === 'top'">
    <mat-icon class="material-icons-outlined" aria-hidden="true">swap_vert</mat-icon>Top-down, wide
  </button>
  <button mat-menu-item (click)="setLayout('left')" [class.selected-layout]="layout === 'left'">
    <mat-icon class="material-icons-outlined" aria-hidden="true">swap_horiz</mat-icon>Left to right
  </button>
</mat-menu>

<mat-menu #pngMenu="matMenu">
  <button mat-menu-item (click)="export('png')">Current view</button>
  <button mat-menu-item (click)="export('png', true)">Fully expanded</button>
</mat-menu>
